import assert from 'assert';
import { ReactEventHandler, ReactNode } from 'react';
import { action, computed, makeObservable, observable } from 'mobx';
import fromPairs from 'lodash/fromPairs';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames/dedupe';
import { BindingProps, StoreNode } from '../../store';
import { Store } from '../../store/store';
import { InputState } from './inputState';

type InputLikeState = InputState | InputGroupState;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- form groups aggregate heterogeneous input values by name
export type InputGroupExport = Record<string, any>;

export type InputGroupStateProps = {
  name: string,
} &

  Partial<{
    onSubmit: ReactEventHandler
    onSubmitResolve: ReactEventHandler,
    onSubmitReject: ReactEventHandler
  }> &

  BindingProps<{

    inputs: InputLikeState[],
    initialInputs: InputLikeState[],

    showStatus: boolean,
    showStatusMessage: boolean,
    status: ReactNode,
    statusMessage: ReactNode,

    info: ReactNode,
    error: ReactNode,
    warning: ReactNode,
    success: ReactNode,
    loading: ReactNode,

    disabled: boolean,
    readOnly: boolean,

    isSubmitDisabled: boolean,
    export: InputGroupExport
  }, InputGroupState>;


/**
 * Allows grouping of inputs and exposes queries and methods for the group as a whole.
 */
export class InputGroupState
  extends StoreNode {

  constructor(store: Store, props: InputGroupStateProps) {
    super(store, props);
    makeObservable(this, {
      inputs: computed,
      readOnly: computed,
      isFocused: computed,
      hasFocusedInputs: computed,
      focusCount: computed,
      inputFocusCount: computed,
      isChanged: computed,
      hasChangedInputs: computed,
      changeCount: computed,
      inputChangeCount: computed,
      isDirty: computed,
      hasDirtyInputs: computed,
      arrayOfInputsChanged: computed,
      isEmpty: computed,
      hasNonEmptyInputs: computed,
      hasInfoInputs: computed,
      hasLoadingInputs: computed,
      hasSuccessInputs: computed,
      hasWarningInputs: computed,
      hasErrorInputs: computed,
      hasVisibleError: computed,
      isTouched: computed,
      hasTouchedInputs: computed,
      isChangedSinceLastSubmit: computed,
      hasTouchedErrorInputs: computed,
      hasChangedSinceLastSubmitInputs: computed,
      hasVisibleErrorInputs: computed,
      hasDisabledInputs: computed,
      hasReadOnlyInputs: computed,
      isSubmitting: observable,
      isSubmitRejected: observable,
      isSubmitResolved: observable,
      isSubmitCompleted: computed,
      submitCount: observable,
      submitResolveCount: observable,
      submitRejectCount: observable,
      isSubmittedOnce: computed,
      isSubmitResolvedOnce: computed,
      isSubmitRejectedOnce: computed,
      isSubmitDisabled: computed,
      canSubmit: computed,
      error: computed,
      warning: computed,
      success: computed,
      loading: computed,
      info: computed,
      showStatus: computed,
      showStatusMessage: computed,
      disabled: computed,
      status: computed,
      statusMessage: computed,
      saveInitialInputs: action,
      reset: action,
      clear: action,
      handleSubmit: action,
      handleSubmitResolve: action,
      handleSubmitReject: action,
    });

    const { name } = props;
    assert(typeof name === 'string' && name.length > 0,
      `InputGroup name must be a non-empty string.`);

    this.name = name;
  }

  readonly name: string;
  initialInputs: InputLikeState[] | null = null;

  get inputs(): InputLikeState[] {
    return this.getResolvedProp('inputs', []);
  }
  get readOnly(): boolean { return false; }

  get isFocused(): boolean { return this.hasFocusedInputs; }
  get hasFocusedInputs(): boolean {
    return this.inputs.some(input => input.isFocused);
  }

  get focusCount(): number { return this.inputFocusCount; }
  get inputFocusCount() {
    return this.inputs.reduce((sum, input) => sum + input.focusCount, 0);
  }

  get isChanged(): boolean { return this.hasChangedInputs; }
  get hasChangedInputs() {
    return this.inputs.some(input => input.isChanged);
  }

  get changeCount(): number { return this.inputChangeCount; }
  get inputChangeCount() {
    return this.inputs.reduce((sum, input) => sum + input.changeCount, 0);
  }


  get isDirty(): boolean { return this.hasDirtyInputs }
  get hasDirtyInputs() {
    return this.inputs.some(input => input.isDirty);
  }

  // Checks if the array of inputs has changed since initialization
  get arrayOfInputsChanged(): boolean {
    if (!this.initialInputs) {
      return this.inputs.some(input => (input as InputGroupState).arrayOfInputsChanged);
    }

    return (
      (!isEqual(this.initialInputs, this.inputs)) ||
      this.inputs.some(input => (input as InputGroupState).arrayOfInputsChanged));
  }

  get isEmpty(): boolean { return !this.hasNonEmptyInputs; }
  get hasNonEmptyInputs() {
    return this.inputs.some(input => !input.isEmpty);
  }

  get hasInfoInputs() {
    return this.inputs.some(input => input.info);
  }
  get hasLoadingInputs() {
    return this.inputs.some(input => input.loading);
  }

  get hasSuccessInputs() {
    return this.inputs.some(input => input.success);
  }

  get hasWarningInputs() {
    return this.inputs.some(input => input.warning);
  }

  get hasErrorInputs() {
    return this.inputs.some(input => input.error);
  }

  get hasVisibleError(): boolean {
    return this.inputs.some(input => input.hasVisibleError);
  }

  get isTouched(): boolean { return this.hasTouchedInputs; }
  get hasTouchedInputs(): boolean {
    return this.inputs.some(input => input.isTouched);
  }

  get isChangedSinceLastSubmit(): boolean {
    return this.hasChangedSinceLastSubmitInputs;
  }

  get hasTouchedErrorInputs() {
    return this.inputs.some(input => input.isTouched && input.error);
  }
  get hasChangedSinceLastSubmitInputs() {
    return this.inputs.some(input => input.isChangedSinceLastSubmit);
  }
  /** 
   * Returns true if there are inputs which have both an 'error' set and 'showStatus' or 'showStatusMessage' set to true. 
   * Useful to decide if the submit button should be disabled, such that it won't be disabled if no obvious error is displayed to the user
   * (even if the data inside the form might be invalid).
   */
  get hasVisibleErrorInputs() {
    return this.inputs.some(input => input.error && (input.showStatus || input.showStatusMessage));
  }

  get hasDisabledInputs() {
    return this.inputs.some(input => input.disabled);
  }

  get hasReadOnlyInputs() {
    return this.inputs.some(input => input.readOnly);
  }

  isSubmitting = false;
  isSubmitRejected = false;
  isSubmitResolved = false;
  get isSubmitCompleted() { return this.isSubmitResolved || this.isSubmitRejected }
  submitCount = 0;
  submitResolveCount = 0;
  submitRejectCount = 0;

  get isSubmittedOnce() { return this.submitCount > 0; }
  get isSubmitResolvedOnce() { return this.submitResolveCount > 0; }
  get isSubmitRejectedOnce() { return this.submitRejectCount > 0; }

  get isSubmitDisabled() {
    return this.getResolvedProp('isSubmitDisabled',
      this.hasTouchedErrorInputs);
  }

  get canSubmit() {
    return !this.hasLoadingInputs && !this.hasErrorInputs;
  }

  get error(): ReactNode | null {
    return this.getResolvedProp('error', (this.hasErrorInputs));
  }
  get warning(): ReactNode | null {
    return this.getResolvedProp('warning');
  }
  get success(): ReactNode | null {
    return this.getResolvedProp('success');
  }
  get loading(): ReactNode | null {
    return this.getResolvedProp('loading');
  }
  get info(): ReactNode | null {
    return this.getResolvedProp('info');
  }

  get showStatus(): boolean {
    return this.getResolvedProp('showStatus', this.hasTouchedInputs)
  }

  get showStatusMessage(): boolean {
    return this.getResolvedProp('showStatusMessage', this.hasTouchedInputs);
  }

  get disabled() {

    const fallback: boolean = (
      this.hasLoadingInputs ||
      this.hasTouchedErrorInputs ||
      this.isSubmitting);

    return this.getResolvedProp('disabled', fallback);
  }

  get status() {
    const fallback = (() => {
      if (this.loading) return 'loading';
      if (this.error) return 'error';
      if (this.warning) return 'warning';
      if (this.success) return 'success';
      if (this.info) return 'info';
      return null;
    })();
    return this.getResolvedProp('status', fallback);
  }

  get statusMessage() {
    const fallback = (() => {
      switch (this.status) {
        case 'loading': return this.loading;
        case 'error': return this.error;
        case 'warning': return this.warning;
        case 'success': return this.success;
        default: return this.info;
      }
    })();
    return this.getResolvedProp('statusMessage', fallback);
  }

  getClassName(props?: { className?: string }, extra?: string | string[] | Record<string, boolean>) {

    const status = (this.showStatus && this.status) || '';
    const statusMessage = (this.showStatusMessage && this.statusMessage) || '';

    return classNames(
      extra,
      props?.className,
      status,
      {
        'status': !!status,
        'status-message': !!statusMessage,
        'focus': this.hasFocusedInputs,
        'dirty': this.hasDirtyInputs,
        'touched': this.hasTouchedInputs,
        'changed': this.hasChangedInputs
      });
  }

  /**
   * Function that stores the initial value of the provided array of inputs
   * Used to keep track of the initial value 
   * in case any comparison with the actual value is needed
   */

  saveInitialInputs() {
    this.initialInputs = [...this.inputs];
  }

  reset() {
    Object.assign(this, {
      submitCount: 0,
      rejectCount: 0,
    });
    this.inputs.forEach(input => input.reset());
  }

  clear() {
    this.reset();
    this.inputs.forEach(input => input.clear());
  }

  export(): InputGroupExport {
    const fallback = fromPairs(
      this.inputs.map(input => [input.name, input.export()]));
    return this.getResolvedProp('export', fallback) as InputGroupExport;
  }

  handleSubmit() {
    this.isSubmitting = true;
    this.isSubmitRejected = false;
    this.isSubmitResolved = false;
    this.submitCount++;
    this.inputs.forEach(input =>
      input.handleSubmit());
  }

  handleSubmitResolve() {
    this.isSubmitting = false;
    this.isSubmitResolved = true;
    this.submitResolveCount++;
    this.inputs.forEach(input =>
      input.handleSubmitResolve());
  }

  handleSubmitReject() {
    this.isSubmitting = false;
    this.isSubmitRejected = true;
    this.submitRejectCount++;
    this.inputs.forEach(input =>
      input.handleSubmitReject());
  }
}

/** Helper method for creating a new InputState object for the provided StoreNode. */
export const inputGroup = (node: StoreNode, props: InputGroupStateProps) => {
  return new InputGroupState(node.store, props);
}
